// 出撃＝国盗りのワールドマップ（GAME_DESIGN 17章）。
// 島の絵地図で攻める国を選ぶ → 地方の詳細（拡大図に5戦の地点＋一覧、式神の候補、出陣・掃討）。
// PC：中央に地図（島／国の拡大図）、右に地方の詳細。スマホ：島の地図が画面の大部分＋下に選んだ国の札、押すと地方の詳細（上に拡大図・下に一覧）。
// お供・出撃キャラ・難易度（上の難易度）・装備ドロップ率2倍・オート周回は地方の詳細に置く。
import {
  BATTLE_CONFIG,
  CHARS,
  CHAR_IDS,
  ENEMIES,
  EQUIPMENT,
  COUNTRIES, COUNTRY_BY_ID, provinceLabel, strongholdLabel,
  PROVINCES,
  PROVINCE_BY_ID,
  START_PROVINCE,
  TUTORIAL_STAGE,
  canSweep as metaCanSweep,
  META_CONFIG,
  sweepCost,
  conqueredCount,
  fieldState,
  isShikigami,
  SHIKIGAMI,
  isStageUnlocked,
  guestStageOpen,
  hardUnlocked,
  castleTurnLimitOf,
  placeOfStage,
  provinceOfStage,
  provinceStages,
  provinceViews,
  slotLabel,
  stageBaseExp,
  stageBaseZeni,
  stageLabel,
  type CharId,
  type ProvinceDef,
  type ProvinceView,
  type StageInfo,
} from '@hyakki/core';
import { suggestPush } from '../ui/push';
import { api, ApiError } from '../api';
import { enemyIconHtml, faceStyle, itemIconStyle, shikiArtHtml } from '../assets';
import { artIcon, icon } from '../ui/icons';

import { hyoroCost, powerOf, recommendedPower, stageById } from '../core-bridge';
import { go, register } from '../router';
import { act, hasEnt, patch, isCleared, levelNow, refreshMe, starsOf, state, subscribe, type Settings } from '../state';
import { clearSaved, loadSaved } from '../battle/controller';
import { GUEST_CHAPTER_MSG, guestBlocked, guestLockHtml } from '../ui/google-link';
import { autoRun, haulOfSweep, nextAutoStage, startAutoRun, stopAutoRun, type AutoLimit } from '../autorun';
import { playSweepFx } from '../ui/sweep-fx';
import { newRecruits, showRecruits } from '../ui/recruit';
import { ticketsOutHint } from '../ui/komedoiya';
import { playHaul } from '../ui/haul-fx';
import { avatarHtml, myAvatar } from '../ui/avatar';
import { esc, fmt, onAct, resIcon, starsHtml } from '../ui/dom';
import { setLeader } from '../ui/leader';
import { bindCompanionActions } from '../ui/companion';
import { bindItemsActions } from '../ui/items';
import { STATE_LABEL, fitMaps, islandHtml, miniIslandSvg, provinceMapHtml } from '../ui/worldmap';
import '../ui/fit';
import { openModal, toast } from '../ui/overlay';
import { errorText } from '../ui/status';
import { isMobileLayout } from '../ui/viewport';

/** 難易度の名前（結果画面でも使う） */
export const DIFF = ['通常', '上', '極', '鬼'];
/** 選択：国・戦場・出撃キャラ・難易度・表示（島／国の拡大図） */
let sel = { prov: '' as string, stageId: 0, charId: 'akari' as CharId, diff: 0, view: 'island' as 'island' | 'province' };

const viewsNow = (): Record<string, ProvinceView> => {
  const p = state.profile;
  const list = p ? provinceViews(p) : PROVINCES.map((x) => ({ id: x.id, state: x.id === START_PROVINCE ? 'open' : 'fog', cleared: 0, stars: 0 }) as ProvinceView);
  return Object.fromEntries(list.map((v) => [v.id, v]));
};
const fieldStateOf = (s: number) => (state.profile ? fieldState(state.profile, s) : s === TUTORIAL_STAGE ? 'open' : 'locked');
const unlocked = (s: number, d = 0) => !!state.profile && isStageUnlocked(state.profile, s, d);
/** 上の難易度が開いていない理由 */
const diffLockText = () => (state.profile && hardUnlocked(state.profile) ? '一つ下の難易度を5回クリアで解放' : '本編をクリアすると解放');

/** その国で次に選ぶ戦場：未クリアで出撃できる最初のもの → 無ければ本城 */
function defaultField(p: ProvinceDef): number {
  const list = provinceStages(p);
  return list.find((s) => !isCleared(s) && fieldStateOf(s) === 'open') ?? (list.find((s) => !isCleared(s)) ?? p.castle);
}

/** 出撃できないときの理由（ボタンの横・札に出す） */
function lockReason(p: ProvinceDef, v: ProvinceView | undefined, stageId: number): string | null {
  if (unlocked(stageId, sel.diff)) return null;
  if (sel.diff > 0 && unlocked(stageId, 0)) return diffLockText();
  if (v?.state === 'fog') return '隣の国を制圧すると攻め込めます';
  if (v?.state === 'frontier') return `${PROVINCE_BY_ID[COUNTRY_BY_ID[p.countryId].regions[0]].fields[4]}を制圧すると進めます`;
  if (v?.state === 'gated') return `${p.gate}国を制圧すると攻め込めます（いま${state.profile ? conqueredCount(state.profile) : 0}国）`;
  if (p.id === START_PROVINCE && !isCleared(TUTORIAL_STAGE)) return `まず${p.fields[0]}を勝ち取りましょう`;
  if (stageId === p.castle) return `合戦を4つすべて勝つと${strongholdLabel(p)}を攻められます`;
  return 'まだ攻められません';
}

register('stages', {
  title: '出撃',
  nav: 'stages',
  mount(el, params) {
    sel.charId = state.oshi; // 出撃キャラ＝TOPのキャラ（1つの設定）
    const q = Number(params.get('stage'));
    const qp = params.get('prov');
    if (q && provinceOfStage(q)) {
      sel.prov = provinceOfStage(q)!.id;
      sel.stageId = q;
      sel.view = 'province';
      sel.diff = 0;
    } else if (qp && PROVINCE_BY_ID[qp]) {
      sel.prov = qp;
      sel.stageId = defaultField(PROVINCE_BY_ID[qp]);
      sel.view = 'province';
    } else if (!sel.prov) {
      const next = state.progress?.next ?? TUTORIAL_STAGE;
      sel.prov = provinceOfStage(next)?.id ?? START_PROVINCE;
      sel.stageId = next;
      sel.view = 'island';
    } else sel.view = 'island';

    let offFit = () => {};
    const draw = () => {
      const views = viewsNow();
      const prov = PROVINCE_BY_ID[sel.prov] ?? PROVINCE_BY_ID[START_PROVINCE];
      if (!provinceStages(prov).includes(sel.stageId)) sel.stageId = defaultField(prov);
      const conquered = state.profile ? conqueredCount(state.profile) : 0;
      offFit();
      el.innerHTML = `
        <div class="page fill stages wm-page" data-view="${sel.view}">
          <section class="wm-main" aria-label="${sel.view === 'island' ? '島の地図' : `${esc(prov.name)}の地図`}">
            ${
              sel.view === 'island'
                ? `<header class="wm-head"><h1 class="wm-title">${artIcon('sortie')}国盗り</h1>${chapterChip()}<span class="wm-count">制圧 <b>${conquered}</b><small>/${COUNTRIES.length}国</small></span></header>
                   <div class="wm-sea">${islandHtml({ views, selected: prov.id, conquered })}</div>
                   <p class="wm-legend" aria-hidden="true"><span class="lg con">制圧</span><span class="lg open">攻め込める</span><span class="lg fog">未踏</span><span class="lg gate">未解放</span></p>`
                : provinceMapHtml(
                    prov,
                    provinceStages(prov).map((s) => ({ stageId: s, state: fieldStateOf(s), stars: starsOf(s) })),
                    sel.stageId,
                    miniIslandSvg(prov.id, views),
                  ) + `<div class="pv-diff ${prov.boss ? 'left' : ''}" role="radiogroup" aria-label="難易度">${diffChips(stageById(sel.stageId)!)}</div>`
            }
          </section>
          ${sel.view === 'island' ? cardHtml(prov, views[prov.id]) : ''}
          <aside class="wm-side panel-paper" aria-label="地方の詳細">${detailHtml(prov, views[prov.id])}</aside>
        </div>`;
      offFit = fitMaps(el);
    };

    draw();
    // TOP のオート周回ボタンから来たら、そのまま設定の札を開く
    if (params.get('auto')) queueMicrotask(() => guestBlocked('オート周回は、Google連携すると使えます。') || autoDialog());
    const offComp = bindCompanionActions(el); // 式神の欄（変更は下から出る札）
    const offItems = bindItemsActions(el); // 道具の欄（変更は下から出る札。保存で profile が変わり描き直る）
    const off = subscribe((ch) => {
      if (ch.has('progress') || ch.has('resources') || ch.has('profile') || ch.has('stamina') || ch.has('entitlements')) draw();
    });
    const offAct = onAct(el, {
      prov: (b) => {
        const id = b.dataset.id!;
        // 選んでいる国をもう一度押したら地方の詳細（拡大図）へ
        if (id === sel.prov && !isMobileLayout()) sel.view = 'province';
        else {
          sel.prov = id;
          sel.stageId = defaultField(PROVINCE_BY_ID[id]);
          sel.diff = 0;
        }
        draw();
      },
      enter: () => {
        sel.view = 'province';
        draw();
      },
      island: () => {
        sel.view = 'island';
        draw();
      },
      region: (b) => {
        sel.prov = b.dataset.id!; sel.stageId = defaultField(PROVINCE_BY_ID[sel.prov]); sel.diff = 0; draw();
      },
      field: (b) => {
        sel.stageId = Number(b.dataset.id);
        // 選んでいる難易度は、その戦場でも開いていればそのまま
        if (diffLocked(stageById(sel.stageId)!, sel.diff)) sel.diff = 0;
        draw();
      },
      char: (b) => {
        sel.charId = b.dataset.id as CharId;
        void setLeader(sel.charId);
        draw();
      },
      diff: (b) => {
        sel.diff = Number(b.dataset.d);
        draw();
      },
      sortie: () => sortie(),
      pickchar: () => pickDialog('char', draw),
      pickdiff: () => pickDialog('diff', draw),
      info: () => infoDialog(stageById(sel.stageId)!),
      sweep: () => (!guestStageOpen(sel.stageId) && guestBlocked(GUEST_CHAPTER_MSG)) || sweepDialog(stageById(sel.stageId)!),
      auto: () => guestBlocked('オート周回は、Google連携すると使えます。') || autoDialog(),
    });
    return () => {
      offFit();
      off();
      offAct();
      offComp();
      offItems();
    };
  },
});

/** スマホの島の地図の下：選んだ国の札 */
function cardHtml(p: ProvinceDef, v: ProvinceView | undefined) {
  const st = v?.state ?? 'fog';
  const recs = provinceStages(p).map((s) => recommendedPower(s, 0) ?? 0);
  const btn =
    st === 'open'
      ? `<button class="btn primary wc-go" data-act="enter">${icon('sortie')}攻める</button>`
      : st === 'conquered'
        ? `<button class="btn wc-go" data-act="enter">${icon('flag')}地方へ</button>`
        : `<button class="btn wc-go ghost" data-act="enter">${icon(st === 'gated' ? 'lock' : 'info')}見る</button>`;
  return `<div class="wm-card panel-paper" aria-live="polite">
    <span class="wc-crest ${st}">${icon(st === 'conquered' ? 'flag' : st === 'open' ? 'sortie' : 'lock')}</span>
    <div class="wc-txt"><b>${esc(provinceLabel(p))}</b><small>${STATE_LABEL[st]}${st === 'gated' ? `・制圧 ${state.profile ? conqueredCount(state.profile) : 0}/${p.gate}国で解放` : ''}</small></div>
    <div class="wc-meta"><span>勝利<b>${v?.cleared ?? 0}</b>/5</span><span><i class="ri ri-star"></i><b>${v?.stars ?? 0}</b>/15</span><span>推奨<b>${fmt(Math.min(...recs))}</b>〜</span></div>
    ${btn}
  </div>`;
}

const CONDS = ['クリア', '戦闘不能なし', '龍脈をすべて占領'];
const enemiesOf = (st: StageInfo) => [...new Set([...(st.bossId ? [st.bossId] : []), ...st.enemyIds])];
const enemyThumbs = (st: StageInfo, size = 52) =>
  enemiesOf(st)
    .map((id) => `<span class="thumb ${id === st.bossId ? 'boss' : ''} ${isShikigami(id) ? 'shiki' : ''}" title="${esc(ENEMIES[id]?.name ?? id)}">${enemyIconHtml(id, size)}</span>`)
    .join('');
function dropThumbs(st: StageInfo) {
  const weapon = EQUIPMENT[`w_${sel.charId}_${Math.min(10, st.chapter)}`];
  return `<span class="thumb drop" title="銭"><i class="ri ri-zeni"></i><small>${fmt(stageBaseZeni(st.id, sel.diff))}</small></span>
    <span class="thumb drop" title="主EXP"><i class="ri ri-exp"></i><small>${fmt(stageBaseExp(st.id, sel.diff))}</small></span>
    <span class="thumb drop item" title="${esc(weapon?.name ?? '装備')}" style="${itemIconStyle(weapon?.id ?? '')}"></span>
    <span class="thumb drop item" title="防具・アクセサリー" style="${itemIconStyle(4)}"></span>`;
}
const diffLocked = (st: StageInfo, i: number) => i > 0 && !unlocked(st.id, i);
function diffChips(st: StageInfo) {
  return DIFF.map((d, i) => {
    const locked = diffLocked(st, i);
    return `<button role="radio" class="chip ${i === sel.diff ? 'on' : ''}" data-act="diff" data-d="${i}" aria-checked="${i === sel.diff}" ${locked ? `disabled title="${diffLockText()}"` : ''}>${locked ? icon('lock') : ''}${d}</button>`;
  }).join('');
}
function charOpts() {
  return CHAR_IDS.map((id) => {
    const c = CHARS[id];
    const eq = state.profile?.sets?.[id];
    const n = eq ? [eq.weapon, eq.armor, eq.netsuke].filter((x) => x != null).length : 0;
    const pw = powerOf(state.profile, id);
    return `<button role="radio" class="char-opt ${id === sel.charId ? 'on' : ''}" data-act="char" data-id="${id}" aria-checked="${id === sel.charId}" title="${c.name}・${c.role}">
      <span class="co-face" style="${faceStyle(id)}"></span>
      <span class="co-aff">${esc(c.role.split('・')[0])}</span>
      <span class="co-eq" aria-label="装備${n}/3">${'<i></i>'.repeat(n)}${'<i class="off"></i>'.repeat(3 - n)}</span>
      <b>${c.name}</b><small>${pw != null ? fmt(pw) : '—'}</small>
    </button>`;
  }).join('');
}

/** 国に出る式神の候補（10種族の素体）。持っていれば★ */
function shikiHtml(p: ProvinceDef) {
  const ids = [...new Set(provinceStages(p).flatMap((s) => enemiesOf(stageById(s)!)))].filter((id) => isShikigami(id));
  ids.sort((a, b) => (SHIKIGAMI[a]?.role === 'boss' ? 1 : 0) - (SHIKIGAMI[b]?.role === 'boss' ? 1 : 0));
  return ids
    .map((id) => {
      const star = state.profile?.shikigami?.[id]?.star ?? 0;
      const boss = SHIKIGAMI[id]?.role === 'boss';
      return `<span class="pv-shiki ${boss ? 'boss' : ''} ${star > 0 ? 'own' : ''}" title="${esc(SHIKIGAMI[id]?.name ?? ENEMIES[id]?.name ?? id)}・${star > 0 ? `仲間 ★${star}` : '式神の候補'}">
        ${shikiArtHtml(id, 46)}</span>`;
    })
    .join('');
}

/** 掃討の対象：この戦場、または国の未クリアの合戦（推奨×sweep.unclearedPowerRatio 以上） */
function sweepable(stageId: number, diff: number): boolean {
  const p = state.profile;
  return !!p && unlocked(stageId, diff) && metaCanSweep(p, stageId, diff).ok;
}
const provinceSweepTargets = (prov: ProvinceDef) => prov.battles.filter((s) => !isCleared(s) && sweepable(s, 0));

function detailHtml(prov: ProvinceDef, v: ProvinceView | undefined) {
  const st = stageById(sel.stageId)!;
  const pl = placeOfStage(st.id)!;
  const cost = hyoroCost(st, sel.diff);
  const rec = recommendedPower(st.id, sel.diff);
  const hyoro = state.stamina ? state.stamina.value + state.stamina.reserve : (state.resources.hyoro ?? 0);
  const enough = hyoro >= cost;
  const p = powerOf(state.profile, sel.charId);
  const reason = lockReason(prov, v, st.id);
  const canSw = sweepable(st.id, sel.diff) || (sel.diff === 0 && provinceSweepTargets(prov).length > 0);
  // 掃討できない理由をボタンの角に：チケット0＝券と0、戦力不足＝鍵
  const swTickets = state.resources.soutou ?? 0;
  const swRes = !canSw && state.profile ? metaCanSweep(state.profile, st.id, sel.diff) : null;
  const swLock = !!swRes && !swRes.ok && swRes.error === 'power';
  const swBadge = swTickets <= 0 ? `<em class="guest-lock sw-tk" aria-label="掃討チケットがありません">${resIcon('soutou')}0</em>` : swLock ? `<em class="guest-lock" aria-label="戦力が足りません">${icon('lock')}</em>` : '';
  const done = v?.cleared ?? 0;
  const rows = provinceStages(prov)
    .map((s, i) => {
      const fs = fieldStateOf(s);
      const castle = i === 4;
      return `<li><button class="pf-row ${s === st.id ? 'on' : ''} ${fs} ${castle ? 'castle' : ''}" data-act="field" data-id="${s}" aria-pressed="${s === st.id}">
        <span class="pf-slot">${castle ? strongholdLabel(prov) : slotLabel(i)}</span>
        <b class="pf-name">${fs === 'locked' ? icon('lock') : ''}${esc(prov.fields[i])}${castle ? `<em class="tag castle">${strongholdLabel(prov)}攻め${castleTurnLimitOf(s) != null ? `<i>${icon('wait')}${castleTurnLimitOf(s)}</i>` : ''}</em>` : stageById(s)?.mode === 'defense' ? '<em class="tag castle defense">防衛</em>' : ''}</b>
        <span class="pf-stars">${starsHtml(starsOf(s), false)}</span>
        <span class="pf-rec ${p != null && (recommendedPower(s, sel.diff) ?? 0) > p ? 'over' : ''}">${fmt(recommendedPower(s, sel.diff))}</span>
        <span class="pf-hy"><i class="ri ri-hyoro"></i>${hyoroCost(stageById(s)!, sel.diff)}</span>
      </button></li>`;
    })
    .join('');
  return `
    <header class="pv-head">
      <span class="pv-crest ${v?.state ?? 'fog'}">${icon('mon')}</span>
      <div class="pv-title"><h2>${esc(provinceLabel(prov))}</h2><small>${STATE_LABEL[v?.state ?? 'fog']}${v?.state === 'gated' ? `・${prov.gate}国制圧で解放` : ''}</small></div>
      <button class="icon-btn pv-i" data-act="info" aria-label="${esc(pl.field)}の詳細">${icon('info')}</button>
      <span class="pv-prog" aria-label="勝利 ${done}/5"><small>勝利</small><b>${done}<i>/5</i></b><span class="pv-pips">${[0, 1, 2, 3, 4].map((i) => `<i class="${i < done ? 'on' : ''}"></i>`).join('')}</span></span>
      <button class="icon-btn pv-mapbtn" data-act="${sel.view === 'island' ? 'enter' : 'island'}" aria-label="${sel.view === 'island' ? '地方の地図を見る' : '島の地図へ戻る'}">${icon(sel.view === 'island' ? 'castle' : 'left')}</button>
    </header>
    <p class="country-commander"><span title="敵将"><small>敵将</small>${icon('oni')}${esc(ENEMIES[COUNTRY_BY_ID[prov.countryId].commander].name)}</span><span title="国の制圧（地方）"><small>地方</small>${icon('flag')}${COUNTRY_BY_ID[prov.countryId].regions.filter(id => viewsNow()[id]?.state === 'conquered').length}/2</span></p>
    <ol class="pv-fields" aria-label="戦場">${rows}</ol>
    ${reason ? `<p class="warn small sd-warn pv-reason">${icon('lock')}${esc(reason)}</p>` : state.suspended ? `<p class="warn small sd-warn">中断中の戦闘があります。出陣すると破棄されます。</p>` : ''}
    <div class="pv-go">
      <button class="pv-me" data-act="pickchar" aria-label="出撃キャラ ${esc(CHARS[sel.charId].name)}・戦力${p != null ? fmt(p) : '—'}（押すと変更）"><span class="co-face" style="${faceStyle(sel.charId)}"></span><em class="sd-pw ${p != null && rec != null ? (p >= rec ? 'good' : 'bad') : ''}">${p != null ? fmt(p) : '—'}</em></button>
      <button class="btn pv-sm sweep-btn" data-act="sweep" ${canSw ? '' : 'disabled'}>${icon('sweep')}<span>掃討</span>${swBadge}</button>
      <button class="btn pv-sm auto-btn" data-act="auto">${icon('auto')}<span>オート</span>${guestLockHtml(icon('lock'))}</button>
      <button class="btn primary big sortie-btn" data-act="sortie" ${enough && !reason ? '' : 'disabled'}>${icon('sortie')}${enough || reason ? '出陣' : '兵糧不足'}</button>
    </div>`;
}

/** スマホ：キャラ・難易度は下から出る札で選ぶ */
function pickDialog(kind: 'char' | 'diff', redraw: () => void) {
  const st = stageById(sel.stageId)!;
  const m = openModal(
    kind === 'char'
      ? `<div class="char-pick pick-sheet" role="radiogroup" aria-label="出撃キャラ">${charOpts()}</div>`
      : `<div class="diff-row pick-sheet" role="radiogroup" aria-label="難易度">${diffChips(st)}</div><p class="muted small">上の難易度は、本編をクリアしたあと、一つ下の難易度を5回クリアで解放されます。</p>`,
    { title: kind === 'char' ? '出撃キャラ' : '難易度', className: 'sheet-wrap navy-sheet' },
  );
  m.body.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-act]');
    if (!b || b.disabled) return;
    if (kind === 'char') {
      sel.charId = b.dataset.id as CharId;
      void setLeader(sel.charId);
    }
    else {
      sel.diff = Number(b.dataset.d);
    }
    m.close();
    redraw();
  });
}

/** 最速手番の番付（いったん出さない） */
const SHOW_RANKING = false;

/** 敵・ドロップ・達成条件（スマホは札に入りきらないのでここで見る） */
function infoDialog(st: StageInfo) {
  const stars = starsOf(st.id);
  const m = openModal(
    `<div class="stage-info">
      <h3 class="sec-title">出現する敵<small>敵Lv ${st.enemyLevel * 10}${st.rivalAi ? `・敵武将の傾向：${esc(BATTLE_CONFIG.rivalAi.types[st.rivalAi].name)}` : ''}</small></h3><div class="sd-enemies">${enemyThumbs(st, 56)}</div>
      <h3 class="sec-title">主なドロップ${hasEnt('drop2x') ? '<small class="sd-drop2">装備ドロップ率2倍</small>' : ''}</h3><div class="sd-drops">${dropThumbs(st)}</div>
      <ul class="sd-conds" aria-label="達成条件">${CONDS.map((t, i) => `<li class="${stars[i] ? 'on' : ''}"><i class="star">★</i>${t}</li>`).join('')}</ul>
      ${((h) => (h ? `<h3 class="sec-title">式神の候補</h3><div class="pv-shikis">${h}</div>` : ''))(shikiHtml(provinceOfStage(st.id)!))}
      ${SHOW_RANKING ? `<h3 class="sec-title">最速手番<small>${sel.diff ? `${DIFF[sel.diff]}・` : ''}戦力が推奨×${META_CONFIG.stageRanking.powerRatio}まで</small></h3><div class="best-body"><div class="spinner"></div></div>` : ''}
    </div>`,
    { title: stageLabel(st.id) },
  );
  if (SHOW_RANKING) renderBest(m.body.querySelector<HTMLElement>('.best-body')!, st.id, sel.diff);
}

/** 中断中の戦闘を破棄する（手元の保存とサーバーの進行中の戦闘の両方。確認は呼び出し側で1回だけ） */
async function abandonSuspended() {
  const sv = loadSaved();
  const act = await api.battleActive().catch(() => null);
  const ids = new Set([sv?.sessionId, act?.session?.sessionId].filter((x): x is string => !!x));
  await Promise.all([...ids].map((id) => api.battleAbandon(id).catch(() => {})));
  clearSaved();
  patch({ suspended: null });
}

/**
 * 中断中の戦闘があるときの確認：戦闘に戻る（いちばん上）・破棄して進む・やめる。
 * 戻るを選んだらここで戦闘へ移り 'resume' を返す
 */
function suspendedChoice(discardLabel: string): Promise<'resume' | 'discard' | null> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: 'resume' | 'discard' | null) => {
      if (done) return;
      done = true;
      m.close();
      resolve(v);
    };
    const m = openModal(
      `<p class="confirm-text">中断中の戦闘があります。</p>
       <div class="suspend-choice">
         <button class="btn primary wide" data-sc="resume">${icon('right')}戦闘に戻る</button>
         <button class="btn danger wide" data-sc="discard">${esc(discardLabel)}</button>
         <button class="btn wide" data-sc="cancel">やめる</button>
       </div>`,
      { title: '確認', onClose: () => finish(null) },
    );
    m.body.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sc]');
      if (!b) return;
      const v = b.dataset.sc;
      if (v === 'resume') {
        finish('resume');
        go('battle', autoRun()?.parked ? { resume: 1, autorun: 1 } : { resume: 1 });
      } else finish(v === 'discard' ? 'discard' : null);
    });
  });
}

async function sortie() {
  if (!guestStageOpen(sel.stageId) && guestBlocked(GUEST_CHAPTER_MSG)) return;
  if (state.suspended) {
    const ok = await suspendedChoice('破棄して出撃');
    if (ok !== 'discard') return;
    await abandonSuspended();
  }
  go('battle', { stage: sel.stageId, char: sel.charId, diff: sel.diff });
}

/** 今の章（次に挑む戦場の章＝国）：「第2章 川霧国」 */
function chapterChip() {
  const ch = stageById(state.progress?.next ?? TUTORIAL_STAGE)?.chapter;
  const c = ch ? COUNTRIES[ch - 1] : undefined;
  return c ? `<span class="wm-count wm-ch">第<b>${ch}</b>章<small>${esc(c.name)}</small></span>` : '';
}

/** 掃討（掃討チケット1枚で1戦。兵糧は使わない。GAME_DESIGN 20章）
 *  回数はスライダー（1〜チケット数・1回の上限まで）と 10・20・50・MAX → 「掃討」→ ちびキャラの小芝居 → 周回の成果と同じ画面 */
function sweepDialog(st: StageInfo) {
  const prov = provinceOfStage(st.id)!;
  const one = sweepable(st.id, sel.diff);
  const targets = sel.diff === 0 ? provinceSweepTargets(prov) : [];
  const tickets = () => state.resources.soutou ?? 0;
  // 上限は持っている掃討チケットの数（サーバーは1回の通信で maxCount 戦までなので、分けて送る）
  const max = () => Math.max(0, tickets());
  const fee = sweepCost(st.id, sel.diff).zeni;
  let n = Math.min(max(), 10) || Math.min(max(), 1);
  const m = openModal(
    `<p class="sweep-wallet">${resIcon('soutou')}掃討チケット <b class="sw-n">${fmt(tickets())}</b></p>
     ${
       one
         ? `<div class="sw-count">
             <p class="sw-target">${esc(stageLabel(st.id))}</p>
             <div class="sw-slide"><input type="range" class="sw-range" min="1" max="${Math.max(1, max())}" value="${Math.max(1, n)}" aria-label="回数" ${max() ? '' : 'disabled'}><b class="sw-v">${n}<small>回</small></b></div>
             <div class="sw-quick">${[10, 20, 50].map((k) => `<button class="chip" data-q="${k}" ${max() >= k ? '' : 'disabled'}>${k}</button>`).join('')}<button class="chip max" data-q="max" ${max() ? '' : 'disabled'}>MAX</button></div>
             <button class="btn primary big sw-go" data-swgo ${max() ? '' : 'disabled'}>${icon('sweep')}<span>掃討</span><small class="sw-cost"></small></button>
           </div>`
         : ''
     }
     ${
       targets.length
         ? `<div class="sweep-prov"><p><b>${esc(prov.name)}の合戦をまとめて掃討</b><br><small class="muted">未クリアの合戦 ${targets.map((s) => esc(placeOfStage(s)!.field)).join('・')}（推奨の${META_CONFIG.sweep.unclearedPowerRatio}倍以上）を、1つずつ勝つまで2回まで。砦・本城は手動で攻めます。</small></p>
           <button class="btn" data-prov="1" ${tickets() > 0 ? '' : 'disabled'}>${icon('sweep')}まとめて掃討<small>${resIcon('soutou')}${targets.length}〜</small></button></div>`
         : ''
     }
     <div class="sweep-result"></div>`,
    { title: '掃討' },
  );
  const range = m.body.querySelector<HTMLInputElement>('.sw-range');
  const setN = (v: number) => {
    n = Math.max(Math.min(1, max()), Math.min(max(), v));
    if (range) range.value = String(n);
    const vEl = m.body.querySelector('.sw-v');
    if (vEl) vEl.innerHTML = `${n}<small>回</small>`;
    const c = m.body.querySelector('.sw-cost');
    if (c) c.innerHTML = `${resIcon('soutou')}${n}${fee ? `・銭${fmt(fee * n)}` : ''}`;
    m.body.querySelectorAll<HTMLElement>('[data-q]').forEach((b) => b.classList.toggle('on', b.dataset.q === 'max' ? n === max() : Number(b.dataset.q) === n));
  };
  setN(n);
  range?.addEventListener('input', () => setN(Number(range.value)));
  const lock = (on: boolean) => m.body.querySelectorAll<HTMLButtonElement | HTMLInputElement>('[data-swgo],[data-prov],[data-q],.sw-range').forEach((x) => (x.disabled = on));
  // 操作ID：通信が切れて同じボタンを押し直したときは同じIDを送る（サーバーが二重に掃討しない）。結果が返ったら作り直す
  let pending: { key: string; id: string } | null = null;
  let busy = false;
  m.body.addEventListener('click', async (e) => {
    const q = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-q]');
    if (q && !busy) return setN(q.dataset.q === 'max' ? max() : Number(q.dataset.q));
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-swgo],[data-prov]');
    if (!b || busy) return;
    busy = true;
    lock(true);
    const whole = !!b.dataset.prov;
    const key = whole ? 'prov' : `n${n}`;
    if (pending?.key !== key) pending = { key, id: crypto.randomUUID() };
    const opId = pending.id;
    const out = m.body.querySelector<HTMLElement>('.sweep-result')!;
    out.innerHTML = '<div class="spinner"></div>';
    const before = { level: levelNow(), power: powerOf(state.profile, sel.charId) };
    const charId = sel.charId;
    // 結果を待つあいだも戦場を落とし続ける小芝居を出す（結果が来たら、その回の決着のあとに成果へ）
    let doneN = 0;
    const req = whole ? act(api.battleSweepProvince(prov.id, charId, 2, opId)) : sweepChunks(st.id, sel.diff, n, charId, opId, (k) => (doneN = k));
    const fx = playSweepFx({ charId, enemyIds: st.enemyIds, terrain: st.terrain, done: req.then((r) => r.runs.length, () => null), progress: () => doneN });
    try {
      const r = await req;
      pending = null;
      await refreshMe().catch(() => {});
      m.close();
      const first = stageById(r.runs[0]?.stageId ?? st.id) ?? st;
      await fx;
      // 掃討で式神が仲間になった：成果（リザルト）の前に小窓
      await showRecruits(newRecruits(r.runs.map((x) => x.rewards)));
      const h = haulOfSweep(r, charId, first.id, before);
      if (h) await playHaul(h);
      await ticketsOutHint();
      if (whole && 'cleared' in r && (r as { cleared: number[] }).cleared.length === 4) toast('本城を攻められます', 'good', 3500);
    } catch (err) {
      // 通信の失敗（届いたか分からない）は同じIDのまま押し直せるようにする。サーバーが答えたもの（足りない・実行済み等）は作り直す
      if (!(err instanceof ApiError && err.status === 0)) pending = null;
      if (err instanceof ApiError && err.code === 'already_done') void refreshMe().catch(() => {});
      await fx;
      out.innerHTML = `<p class="bad">${esc(errorText(err))}</p>`;
      lock(false);
    } finally {
      busy = false;
    }
  });
}

/** 掃討を10戦ずつに分けて送り、結果をまとめる（途中で失敗したら、そこまでの結果を返す。最初で失敗したら投げる） */
async function sweepChunks(stageId: number, diff: number, n: number, charId: CharId, opId: string, onProgress?: (done: number) => void) {
  // 1回の通信は25戦まで（サーバーは1戦ずつ戦闘を計算する。1戦およそ20ms、処理時間の上限は wrangler.jsonc の limits.cpu_ms）。
  // 失敗した組は同じ操作IDで少し待って送り直す（503 なら何も反映されていない。届いて実行済みなら already_done で、その組は済んだものとして続ける）
  const per = Math.min(25, META_CONFIG.sweep.maxCount);
  let out: Awaited<ReturnType<typeof api.battleSweep>> | null = null;
  for (let done = 0, i = 0; done < n; i++) {
    const k = Math.min(per, n - done);
    const id = i ? crypto.randomUUID() : opId;
    let r: Awaited<ReturnType<typeof api.battleSweep>> | null = null;
    let skip = false;
    for (let t = 0; ; t++) {
      try {
        r = await act(api.battleSweep(stageId, diff, k, charId, id));
        break;
      } catch (e) {
        if (e instanceof ApiError && e.code === 'already_done') {
          skip = true;
          break;
        }
        const retry = e instanceof ApiError && (e.status === 0 || e.status >= 500) && t < 3;
        if (!retry) {
          if (!out) throw e;
          // 途中で止まった：黙って少ない結果を出さず、何戦で止まったかを知らせる（残りのチケットは減っていない）
          toast(`${out.runs.length}戦で止まりました（${errorText(e)}）。残りのチケットは使っていません`, 'error', 5000);
          return out;
        }
        await new Promise((res) => setTimeout(res, 800 * (t + 1)));
      }
    }
    done += k;
    if (skip || !r) continue;
    out = out ? { ...r, runs: [...out.runs, ...r.runs], ticketsSpent: out.ticketsSpent + r.ticketsSpent, zeniFee: out.zeniFee + r.zeniFee, itemsGained: out.itemsGained + r.itemsGained } : r;
    onProgress?.(out.runs.length);
    if (r.runs.length < k) break; // チケット・銭が尽きた
  }
  // 押し直したが、どの組もすでに反映済みだった（応答だけ届かなかった）：反映済みとして扱う（呼び出し側が所持を読み直す）
  if (!out) throw new ApiError(409, 'already_done');
  return out;
}

/** 最速手番の番付（この戦場・この難易度）。手番が同じなら先に出した人が上。戦力も並べて育成の差が分かるように */
function renderBest(body: HTMLElement, stageId: number, diff: number) {
  const me = state.me?.user.id;
  api
    .stageRanking(stageId, diff)
    .then((r) => {
      body.innerHTML = `${r.top.length ? `<ol class="rank-list best-list fit-scroll">${r.top
        .map((x) => `<li class="${x.userId === me ? 'me' : ''} ${x.rank <= 3 ? `top${x.rank}` : ''}"><span class="rk">${x.rank}</span><span class="rface">${avatarHtml(x.avatar, 40)}</span><b class="nm"></b><span class="rv">${x.turns}<small>手番</small></span><span class="rv2">${x.power ? `戦力${fmt(x.power)}` : ''}</span></li>`)
        .join('')}</ol>` : ''}
        ${r.me ? `<div class="my-rank"><span class="rk">${r.me.rank}</span><span class="rface">${avatarHtml(myAvatar(), 40)}</span><b>あなた</b><span class="rv">${r.me.turns}<small>手番</small></span></div>` : `<div class="my-rank"><span class="rk">—</span><span class="rface">${avatarHtml(myAvatar(), 40)}</span><b>あなた</b><span class="rv">記録なし</span></div>`}`;
      // 表示名はユーザー入力なので textContent で
      body.querySelectorAll('.best-list .nm').forEach((n, i) => (n.textContent = r.top[i].name));
    })
    .catch((e) => (body.innerHTML = `<p class="bad">${esc(errorText(e))}</p>`));
}

/** オート周回の終了条件の選択肢（回数・時刻・兵糧が尽きるまで） */
const AUTO_COUNTS = [5, 10, 30, 50, 100];
/** 負けて「ひとつ手前に戻る」：手前で何勝したら挑み直すか（autorun.ts の BACK_WINS と同じ） */
const BACK_WINS = 5;

function autoDialog() {
  const s = state.settings;
  const cleared = (state.progress?.cleared ?? []).slice().sort((a, b) => b - a);
  const lim = s.autoLimit ?? { kind: 'count', n: 10 };
  const selName = stageLabel(sel.stageId, { short: true });
  // 周回先：選択中の戦場（既定）／直近の勝利先／クリア済みの戦場。旧設定（autoTarget）も読む
  const farm: 'selected' | 'last' | number = s.autoFarm ?? (s.autoTarget != null ? s.autoTarget : 'selected');
  const advance = (s.autoRetryEvery ?? 5) > 0;
  const onLose = s.autoOnLose ?? 'retry';
  let retryTimes = Math.max(1, Math.min(10, s.autoRetryTimes ?? 3));
  const limVal = lim.kind === 'count' ? `n${lim.n}` : lim.kind === 'until' ? 'until' : 'none';
  const m = openModal(
    `<form class="auto-form">
      <p class="muted small">この画面を開いている間（閉じてから10分まで）動きます。兵糧が尽きたら回復を待って続けます。出撃は<b>${esc(CHARS[sel.charId].name)}</b>（難易度は通常）。</p>
      <div class="af-grid">
        <label><span>未クリアへ</span><select name="every">
          <option value="1" ${advance ? 'selected' : ''}>挑戦する</option>
          <option value="0" ${advance ? '' : 'selected'}>挑戦しない</option>
        </select></label>
        <label class="af-farm"><span>周回先</span><select name="farm">
          <option value="selected" ${farm === 'selected' ? 'selected' : ''}>選択中（${esc(selName)}）</option>
          <option value="last" ${farm === 'last' ? 'selected' : ''}>直近の勝利先</option>
          ${cleared.map((id) => `<option value="${id}" ${farm === id ? 'selected' : ''}>${esc(stageLabel(id, { short: true }))}</option>`).join('')}
        </select></label>
        <label><span>負けたとき</span><select name="lose">
          <option value="stop" ${onLose === 'stop' ? 'selected' : ''}>止める</option>
          <option value="back" ${onLose === 'back' ? 'selected' : ''}>ひとつ手前に戻る</option>
          <option value="retry" ${onLose === 'retry' ? 'selected' : ''}>再挑戦する</option>
        </select></label>
        <div class="af-retry"><span>再挑戦</span><span class="af-step"><button type="button" class="icon-btn" data-step="-1" aria-label="減らす">−</button><b class="af-n">${retryTimes}</b><button type="button" class="icon-btn" data-step="1" aria-label="増やす">＋</button><small>回負けたら止める</small></span></div>
        <label><span>終了</span><span class="af-limit"><select name="limit">
          ${AUTO_COUNTS.map((n) => `<option value="n${n}" ${limVal === `n${n}` ? 'selected' : ''}>${n}戦</option>`).join('')}
          <option value="until" ${limVal === 'until' ? 'selected' : ''}>時刻まで</option>
          <option value="none" ${limVal === 'none' ? 'selected' : ''}>止めるまで</option>
        </select><input type="time" name="until" value="${lim.kind === 'until' ? esc(lim.hhmm) : ''}" aria-label="終了時刻" ${limVal === 'until' ? '' : 'hidden'}></span></label>
      </div>
      <p class="af-flow small" aria-live="polite"></p>
      <fieldset class="af-goals"><legend>戦い方（オートの目標）</legend>
        <label class="check"><input type="checkbox" name="shiki" ${s.autoShiki ? 'checked' : ''}> 式神候補を必ず倒す<small>式神になる妖を倒してから敵本陣へ</small></label>
        <label class="check"><input type="checkbox" name="relays" ${s.autoRelays ? 'checked' : ''}> 龍脈をすべて取る<small>★3の条件。全部取ってから敵本陣へ</small></label>
        <small class="muted">負けそうなとき・延長戦では勝利を優先します。戦闘中のオートでも同じ設定を使います。</small>
      </fieldset>
      <label class="check"><input type="checkbox" name="story" ${s.autoStory ? 'checked' : ''}> 物語の場面を見る</label>
      <label class="check af-test"><input type="checkbox" name="fast" ${s.autoFast ? 'checked' : ''}> 節電モード<small>盤面を描かずに結果だけ表示</small></label>
      <p class="muted small">戦闘画面の「このバトルで終わる」でいつでも止められます。</p>
      <div class="btn-row"><button class="btn" type="button" data-close>閉じる</button><button class="btn primary">開始</button></div>
    </form>`,
    { title: 'オート周回' },
  );
  const form = m.body.querySelector<HTMLFormElement>('form')!;
  // 流れの説明（周回先と再挑戦の組み合わせ）を選択に合わせて1行で
  const flow = () => {
    const f = new FormData(form);
    const fv = String(f.get('farm'));
    const name = fv === 'selected' ? `「${selName}」` : fv === 'last' ? '直近の勝利先' : `「${stageLabel(Number(fv), { short: true })}」`;
    const adv = f.get('every') === '1';
    const lose = String(f.get('lose'));
    (form.querySelector('.af-farm') as HTMLElement).hidden = adv;
    (form.querySelector('.af-retry') as HTMLElement).hidden = lose !== 'retry';
    const head = adv ? '未クリアの戦場へ順に進む' : `${name}を周回`;
    const tail = lose === 'stop' ? '負けたら止める' : lose === 'back' ? `負けたらひとつ手前の戦場で${BACK_WINS}勝してから挑み直す（手前でも負けが続いたら止める）` : `負けたら同じ戦場に再挑戦（${retryTimes}回続けて負けたら止める）`;
    form.querySelector('.af-flow')!.textContent = `流れ：${head} → ${tail}`;
    const until = form.querySelector<HTMLInputElement>('input[name="until"]')!;
    until.hidden = f.get('limit') !== 'until';
  };
  form.addEventListener('change', flow);
  // 再挑戦の回数（1〜10）
  form.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-step]');
    if (!b) return;
    retryTimes = Math.max(1, Math.min(10, retryTimes + Number(b.dataset.step)));
    form.querySelector('.af-n')!.textContent = String(retryTimes);
    form.querySelectorAll<HTMLButtonElement>('[data-step]').forEach((x) => (x.disabled = (Number(x.dataset.step) < 0 && retryTimes <= 1) || (Number(x.dataset.step) > 0 && retryTimes >= 10)));
    flow();
  });
  flow();
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const f = new FormData(form);
    const fv = String(f.get('farm'));
    const farmSet: 'selected' | 'last' | number = fv === 'selected' || fv === 'last' ? fv : Number(fv);
    const target = farmSet === 'selected' ? sel.stageId : farmSet === 'last' ? null : farmSet;
    const every = f.get('every') === '1' ? 5 : 0;
    const onLose = String(f.get('lose')) as 'stop' | 'back' | 'retry';
    const lv = String(f.get('limit'));
    let limit: AutoLimit = { kind: 'none' };
    let saveLimit: Settings['autoLimit'] = { kind: 'none' };
    if (/^n\d+$/.test(lv)) {
      const n = Number(lv.slice(1));
      limit = { kind: 'count', n };
      saveLimit = { kind: 'count', n };
    } else if (lv === 'until') {
      const hhmm = String(f.get('until') ?? '');
      const mm = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
      if (!mm) return toast('終了時刻を入れてください', 'error');
      const d = new Date();
      d.setHours(Number(mm[1]), Number(mm[2]), 0, 0);
      if (d.getTime() <= Date.now()) d.setDate(d.getDate() + 1); // 過ぎていれば翌日のその時刻
      limit = { kind: 'until', at: d.getTime() };
      saveLimit = { kind: 'until', hhmm };
    }
    // 節電モード（盤面を描かない。1戦の時間は同じ）
    const fast = f.get('fast') === 'on';
    const ai = { shiki: f.get('shiki') === 'on', relays: f.get('relays') === 'on' };
    const story = f.get('story') === 'on';
    patch({
      settings: { ...state.settings, autoFarm: farmSet, autoTarget: typeof farmSet === 'number' ? farmSet : null, autoRetryEvery: every, autoLimit: saveLimit, autoShiki: ai.shiki, autoRelays: ai.relays, autoFast: fast, autoStory: story, autoOnLose: onLose, autoRetryTimes: retryTimes },
    });
    if (state.suspended) {
      const ok = (await suspendedChoice('破棄して開始')) === 'discard';
      if (!ok) return;
      await abandonSuspended();
    }
    // アプリで初めて周回するとき：閉じたあとの結果の通知をすすめる（答えてから始める）
    await suggestPush('farm');
    startAutoRun({ charId: sel.charId, target, retryEvery: every, limit, fast, ai, story, onLose, retryTimes });
    const next = nextAutoStage();
    if ('stop' in next) {
      stopAutoRun(next.stop);
      toast(next.stop, 'error', 3500);
      return;
    }
    if (next.waitMs) {
      stopAutoRun('兵糧が足りません');
      toast('兵糧が足りません', 'error', 3500);
      return;
    }
    m.close();
    go('battle', { stage: next.stageId, char: sel.charId, diff: 0, autorun: 1 });
  });
}
